import { useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Banknote,
  CheckCircle2,
  XCircle,
  Clock,
  ShieldCheck,
  Loader2,
  Send,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminListWithdrawals,
  adminApproveWithdrawal,
  adminMarkWithdrawalPaid,
  adminRejectWithdrawal,
  adminPaystackTransfersStatus,
} from "@/lib/afritask";
import { formatApiError } from "@/lib/errors";

const fmtNaira = (n) => `₦${Number(n).toLocaleString("en-NG")}`;
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "pending_manual", label: "Awaiting manual payout" },
  { key: "processing", label: "Processing" },
  { key: "paid", label: "Paid" },
  { key: "rejected", label: "Rejected" },
  { key: "failed", label: "Failed" },
  { key: "cancelled", label: "Cancelled" },
  { key: "all", label: "All" },
];

const STATUS_META = {
  pending: { tone: "bg-amber-50 text-amber-700 border-amber-200", Icon: Clock, label: "Pending" },
  pending_manual: { tone: "bg-amber-50 text-amber-800 border-amber-200", Icon: ShieldCheck, label: "Manual" },
  processing: { tone: "bg-amber-50 text-amber-800 border-amber-200", Icon: Loader2, label: "Processing" },
  paid: { tone: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2, label: "Paid" },
  rejected: { tone: "bg-red-50 text-red-700 border-red-200", Icon: XCircle, label: "Rejected" },
  failed: { tone: "bg-red-50 text-red-700 border-red-200", Icon: XCircle, label: "Failed" },
  cancelled: { tone: "bg-neutral-100 text-neutral-600 border-neutral-200", Icon: XCircle, label: "Cancelled" },
};

export default function AdminWithdrawals() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [approveTarget, setApproveTarget] = useState(null);
  const [markPaidTarget, setMarkPaidTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);

  const [manualRef, setManualRef] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);

  // Paystack merchant-account Transfers status — drives the persistent banner.
  const [transfersStatus, setTransfersStatus] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const d = await adminListWithdrawals(status);
      setRows(d.withdrawals || []);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load withdrawals"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Fetch Paystack Transfers merchant-account status once on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await adminPaystackTransfersStatus();
        if (!cancelled) setTransfersStatus(s);
      } catch {
        // Silent — banner just won't render. Approve flow still works.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const counts = useMemo(() => {
    return rows.reduce((acc, r) => {
      acc[r.status] = (acc[r.status] || 0) + 1;
      return acc;
    }, {});
  }, [rows]);

  const doApprove = async () => {
    setBusy(true);
    try {
      const resp = await adminApproveWithdrawal(approveTarget.withdrawal_id);
      if (resp.status === "pending_manual") {
        const detail = resp.reason_detail ? `: ${resp.reason_detail}` : "";
        const reasonMap = {
          flag_disabled:
            "Auto-approve is OFF. Approved — pay manually then mark paid.",
          paystack_transfers_not_enabled:
            "Paystack rejected — Transfers not activated on your Paystack account. Approved — pay manually then mark paid.",
          paystack_call_failed:
            `Paystack call failed${detail}. Approved — pay manually then mark paid.`,
          no_recipient:
            "User's bank not onboarded to Paystack. Approved — pay manually then mark paid.",
        };
        toast.info(reasonMap[resp.reason] || "Approved — pay manually then mark paid.");
      } else if (resp.status === "processing") {
        toast.success("Approved — Paystack transfer initiated");
      } else {
        toast.success("Approved");
      }
      setApproveTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e, "Approve failed"));
    } finally {
      setBusy(false);
    }
  };

  const doMarkPaid = async () => {
    setBusy(true);
    try {
      await adminMarkWithdrawalPaid(markPaidTarget.withdrawal_id, manualRef || null);
      toast.success("Marked as paid — user notified");
      setMarkPaidTarget(null);
      setManualRef("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mark-paid failed");
    } finally {
      setBusy(false);
    }
  };

  const doReject = async () => {
    if (!rejectReason.trim()) return;
    setBusy(true);
    try {
      await adminRejectWithdrawal(rejectTarget.withdrawal_id, rejectReason.trim());
      toast.success("Rejected — refund posted to user's earnings wallet");
      setRejectTarget(null);
      setRejectReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      {transfersStatus && transfersStatus.transfers_enabled === false && (
        <div
          data-testid="paystack-transfers-disabled-banner"
          role="status"
          className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <div className="space-y-1">
            <p>
              <span className="font-semibold">
                Paystack Transfers not enabled on your merchant account.
              </span>{" "}
              Auto-payout will fall back to manual until you activate Transfers
              in your Paystack dashboard.
            </p>
            {transfersStatus.last_error && (
              <p className="text-xs text-amber-800/80">
                Paystack said: <span className="font-mono">{transfersStatus.last_error}</span>
              </p>
            )}
            <a
              href="https://paystack.com/docs/transfers/single-transfers"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-medium text-amber-900 underline hover:text-amber-950"
              data-testid="paystack-transfers-learn-link"
            >
              Learn how →
            </a>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-neutral-500">Payouts via Paystack Transfers · falls back to manual bank transfer if disabled</p>
        </div>
        <Badge variant="outline" className="gap-1 border-emerald-200 bg-emerald-50 text-emerald-700">
          <Banknote className="h-3 w-3" /> {rows.length} in view
        </Badge>
      </div>

      <Tabs value={status} onValueChange={setStatus}>
        <TabsList className="w-full flex-wrap">
          {TABS.map((t) => (
            <TabsTrigger
              key={t.key}
              value={t.key}
              data-testid={`withdrawals-tab-${t.key}`}
            >
              {t.label}
              {t.key !== "all" && counts[t.key] > 0 && (
                <span className="ml-1.5 rounded-full bg-neutral-200 px-1.5 text-[10px] font-semibold">
                  {counts[t.key]}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="p-0">
          <Table data-testid="admin-withdrawals-table">
            <TableHeader>
              <TableRow>
                <TableHead>Requested</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Bank</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} className="py-10 text-center text-sm text-neutral-500">
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-10 text-center text-sm text-neutral-500">
                    Nothing in this queue.
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.map((w) => {
                const meta = STATUS_META[w.status] || STATUS_META.pending;
                const Icon = meta.Icon;
                const canApprove = w.status === "pending";
                const canMarkPaid = ["pending", "pending_manual", "processing"].includes(w.status);
                const canReject = ["pending", "pending_manual"].includes(w.status);
                return (
                  <TableRow key={w.withdrawal_id} data-testid={`admin-withdrawal-row-${w.withdrawal_id}`}>
                    <TableCell className="text-xs text-neutral-500 whitespace-nowrap">
                      {fmtWhen(w.requested_at)}
                    </TableCell>
                    <TableCell className="text-xs">
                      <p className="font-medium text-neutral-900">{w.username}</p>
                      <p className="font-mono text-[10px] text-neutral-400">{w.user_id.slice(0, 8)}…</p>
                    </TableCell>
                    <TableCell className="text-xs">
                      <p className="font-medium text-neutral-900">{w.bank_name}</p>
                      <p className="text-neutral-500">{w.account_number_masked}</p>
                      <p className="text-[10px] text-neutral-400">{w.account_holder_name}</p>
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">
                      {fmtNaira(w.amount_ngn)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`gap-1 ${meta.tone}`}>
                        <Icon className={`h-3 w-3 ${w.status === "processing" ? "animate-spin" : ""}`} />
                        {meta.label}
                      </Badge>
                      {w.admin_note && (
                        <p className="mt-1 max-w-[220px] text-[10px] italic text-neutral-500">
                          {w.admin_note}
                        </p>
                      )}
                      {w.rejection_reason && (
                        <p className="mt-1 text-[10px] text-red-500">{w.rejection_reason}</p>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-wrap items-center justify-end gap-1.5">
                        {canApprove && (
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => setApproveTarget(w)}
                            data-testid={`approve-${w.withdrawal_id}`}
                          >
                            Approve
                          </Button>
                        )}
                        {canMarkPaid && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setMarkPaidTarget(w)}
                            data-testid={`mark-paid-${w.withdrawal_id}`}
                          >
                            <Send className="mr-1 h-3 w-3" /> Mark paid
                          </Button>
                        )}
                        {canReject && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setRejectTarget(w)}
                            data-testid={`reject-${w.withdrawal_id}`}
                            className="border-red-200 text-red-600 hover:bg-red-50"
                          >
                            Reject
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ------- Approve dialog ------- */}
      <Dialog open={!!approveTarget} onOpenChange={(v) => !v && setApproveTarget(null)}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve withdrawal</DialogTitle>
            <DialogDescription>
              This will attempt a Paystack Transfer to the user&apos;s bank. If Paystack Transfers are not enabled on the test account, the withdrawal will be marked <span className="font-mono">pending_manual</span> and you can complete the payout by bank transfer before marking it paid.
            </DialogDescription>
          </DialogHeader>
          {approveTarget && (
            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-sm">
              <p className="font-semibold">
                {fmtNaira(approveTarget.amount_ngn)} → {approveTarget.bank_name}
              </p>
              <p className="text-xs text-neutral-500">
                {approveTarget.account_holder_name} · {approveTarget.account_number_masked}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveTarget(null)}>
              Cancel
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={doApprove}
              disabled={busy}
              data-testid="confirm-approve-btn"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Approve & attempt transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------- Mark paid dialog ------- */}
      <Dialog
        open={!!markPaidTarget}
        onOpenChange={(v) => {
          if (!v) {
            setMarkPaidTarget(null);
            setManualRef("");
          }
        }}
      >
        <DialogContent data-testid="mark-paid-dialog">
          <DialogHeader>
            <DialogTitle>Mark as paid</DialogTitle>
            <DialogDescription>
              Confirm you have transferred the funds. The user gets a &ldquo;Withdrawal sent&rdquo; notification.
            </DialogDescription>
          </DialogHeader>
          {markPaidTarget && (
            <div className="space-y-3">
              <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3 text-sm">
                <p className="font-semibold">
                  {fmtNaira(markPaidTarget.amount_ngn)} → {markPaidTarget.bank_name}
                </p>
                <p className="text-xs text-neutral-500">
                  {markPaidTarget.account_holder_name} · {markPaidTarget.account_number_masked}
                </p>
              </div>
              <div className="space-y-1.5">
                <Label>Bank reference (optional)</Label>
                <Input
                  value={manualRef}
                  onChange={(e) => setManualRef(e.target.value)}
                  placeholder="e.g. TRF-2026-0219-XYZ"
                  data-testid="manual-transfer-ref-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkPaidTarget(null)}>
              Cancel
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={doMarkPaid}
              disabled={busy}
              data-testid="confirm-mark-paid-btn"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Confirm paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------- Reject dialog ------- */}
      <Dialog
        open={!!rejectTarget}
        onOpenChange={(v) => {
          if (!v) {
            setRejectTarget(null);
            setRejectReason("");
          }
        }}
      >
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject withdrawal</DialogTitle>
            <DialogDescription>
              The frozen amount will be refunded to the user&apos;s Earnings Wallet with a ledger entry.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Reason (shown to the user)</Label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="e.g. Account details don't match the profile name."
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={doReject}
              disabled={busy || !rejectReason.trim()}
              data-testid="confirm-reject-btn"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Reject & refund
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
